import React, { useState, useCallback } from 'react';
import { CachedImage } from './CachedImage';
import { CanvasItemHoverOverlay } from './CanvasItemHoverOverlay';
import { useCanvasReactions } from '@/hooks/useCanvasReactions';
import { useAuth } from '@/contexts/AuthContext';

// Use the same CanvasItem type as the main component
interface CanvasItem {
  id: string;
  type: 'sticker' | 'photo' | 'text';
  position: { x: number; y: number };
  rotation?: number;
  zIndex?: number;
  data: any;
}

interface CanvasImageItemProps {
  item: CanvasItem;
  isDragging: boolean;
  onMouseDown: (e: React.MouseEvent | React.TouchEvent, item: CanvasItem) => void;
  onRotate?: (rotation: number) => void;
  onRotateEnd?: () => void;
  onDelete?: (itemId: string) => void;
  onLongPressStart?: (e: React.MouseEvent | React.TouchEvent) => void;
  onLongPressMove?: (e: React.MouseEvent | React.TouchEvent) => void;
  onLongPressEnd?: () => void;
}

export const CanvasImageItem: React.FC<CanvasImageItemProps> = React.memo(({
  item,
  isDragging,
  onMouseDown,
  onRotate,
  onRotateEnd,
  onDelete,
  onLongPressStart,
  onLongPressMove,
  onLongPressEnd,
}) => {
  const { user } = useAuth();
  const { toggleLike } = useCanvasReactions(item.id);
  const [isHovered, setIsHovered] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [rotationStart, setRotationStart] = useState({ angle: 0, clientX: 0, clientY: 0 });
  const [touchStart, setTouchStart] = useState<{ touches: React.Touch[]; rotation: number } | null>(null);
  const [lastClickTime, setLastClickTime] = useState(0);
  
  const isOwnItem = user?.id === item.data?.ownerId || user?.id === (item as any).userId;

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Check for double-click to like (only on other users' items)
    if (!isOwnItem) {
      const now = Date.now();
      if (now - lastClickTime < 300) {
        // Double click detected
        e.preventDefault();
        e.stopPropagation();
        toggleLike();
        return;
      }
      setLastClickTime(now);
    }
    
    // Start long press for pie menu (only for photos, not stickers)
    if (onLongPressStart && item.type === 'photo') {
      onLongPressStart(e);
    }
    
    // Only allow dragging own items
    if (isOwnItem) {
      onMouseDown(e, item);
      e.preventDefault();
    }
  }, [item, onMouseDown, isOwnItem, lastClickTime, toggleLike, onLongPressStart]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Prevent default but allow event to bubble for other handlers
  }, []);
  
  const handleMouseUp = useCallback(() => {
    if (onLongPressEnd) {
      onLongPressEnd();
    }
  }, [onLongPressEnd]);

  // Handle rotation handle mouse down
  const handleRotationMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!onRotate) return;
    
    setIsRotating(true);
    
    // Get the center of the image container (the parent div)
    const itemElement = document.querySelector(`[data-item-id="${item.id}"]`);
    if (!itemElement) return;
    
    const rect = itemElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Calculate initial angle from center to mouse
    const initialAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    
    setRotationStart({
      angle: (item.rotation || 0) - initialAngle,
      clientX: e.clientX,
      clientY: e.clientY
    });
  }, [item.rotation, item.id, onRotate]);

  // Handle rotation mouse move
  const handleRotationMouseMove = useCallback((e: MouseEvent) => {
    if (!isRotating || !onRotate) return;
    
    // Get the center of the image (we need to calculate this from the item position)
    const itemElement = document.querySelector(`[data-item-id="${item.id}"]`);
    if (!itemElement) return;
    
    const rect = itemElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    // Calculate current angle from center to mouse
    const currentAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    
    // Calculate new rotation relative to the starting angle
    const newRotation = rotationStart.angle + currentAngle;
    
    onRotate(newRotation);
  }, [isRotating, onRotate, rotationStart.angle]);

  // Handle rotation mouse up
  const handleRotationMouseUp = useCallback(() => {
    setIsRotating(false);
    if (onRotateEnd) {
      onRotateEnd();
    }
  }, [onRotateEnd]);

  // Touch gesture handlers for mobile rotation and dragging
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && onRotate) {
      // Two-finger rotation
      e.preventDefault();
      const touches = Array.from(e.touches);
      setTouchStart({
        touches,
        rotation: item.rotation || 0
      });
    } else if (e.touches.length === 1) {
      // Single-finger drag - pass the touch event directly
      onMouseDown(e, item);
    }
  }, [item.rotation, onRotate, onMouseDown, item]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && touchStart && onRotate) {
      e.preventDefault();
      
      const touches = Array.from(e.touches);
      
      // Calculate angle between two touches at start
      const startAngle = Math.atan2(
        touchStart.touches[1].clientY - touchStart.touches[0].clientY,
        touchStart.touches[1].clientX - touchStart.touches[0].clientX
      );
      
      // Calculate current angle between two touches
      const currentAngle = Math.atan2(
        touches[1].clientY - touches[0].clientY,
        touches[1].clientX - touches[0].clientX
      );
      
      // Calculate rotation difference in degrees
      const rotationDiff = (currentAngle - startAngle) * (180 / Math.PI);
      const newRotation = touchStart.rotation + rotationDiff;
      
      onRotate(newRotation);
    }
  }, [touchStart, onRotate]);

  const handleTouchEnd = useCallback(() => {
    if (touchStart && onRotateEnd) {
      onRotateEnd();
    }
    setTouchStart(null);
  }, [touchStart, onRotateEnd]);

  // Add/remove event listeners for rotation
  React.useEffect(() => {
    if (isRotating) {
      document.addEventListener('mousemove', handleRotationMouseMove);
      document.addEventListener('mouseup', handleRotationMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleRotationMouseMove);
        document.removeEventListener('mouseup', handleRotationMouseUp);
      };
    }
  }, [isRotating, handleRotationMouseMove, handleRotationMouseUp]);

  return (
    <div
      className="absolute"
      data-item-id={item.id}
      style={{
        left: -60,
        top: -60,
        width: item.data.width + 120,
        height: item.data.height + 120,
        // Don't set zIndex here - let parent handle it to avoid stacking context issues
        transform: `rotate(${item.rotation || 0}deg) scale(${isDragging ? 1.05 : 1})`,
        transformOrigin: 'center center',
        transition: isDragging || isRotating ? 'none' : 'transform 0.2s ease-out',
        cursor: isDragging ? 'grabbing' : 'grab',
        filter: isDragging ? 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))' : 'none',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={(e) => {
        if (onLongPressMove && item.type === 'photo') {
          onLongPressMove(e);
        }
      }}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={(e) => {
        handleTouchStart(e);
        // Start long press for pie menu on touch (only for photos)
        if (onLongPressStart && item.type === 'photo') {
          onLongPressStart(e);
        }
      }}
      onTouchMove={(e) => {
        handleTouchMove(e);
        // Track touch movement to cancel long press if dragging
        if (onLongPressMove && item.type === 'photo') {
          onLongPressMove(e);
        }
      }}
      onTouchEnd={(e) => {
        handleTouchEnd();
        if (onLongPressEnd) {
          onLongPressEnd();
        }
      }}
    >
      {/* Main clickable container */}
      <div
        className={`sticker-container ${isHovered ? 'hovered' : ''} ${isDragging ? 'dragging' : ''} ${item.type === 'photo' ? 'image-item' : 'css-sticker-item'}`}
        style={{
          position: 'absolute',
          left: 60,
          top: 60,
          width: item.data.width,
          height: item.data.height,
          // Enable pointer events on the container for reliable clicking
          pointerEvents: 'auto',
          borderRadius: item.type === 'photo' ? '1rem' : '0',
          // Always allow overflow for sticker effects (flap, shadow)
          overflow: 'visible'
        }}
      >
        {/* Main image */}
        <div className="sticker-main no-lighting" style={{ position: 'relative' }}>
          {/* Loading indicator for images still loading dimensions */}
          {item.data?.isLoading && (
            <div 
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '1rem',
                zIndex: 10,
                pointerEvents: 'none'
              }}
            >
              <div style={{ 
                width: '24px', 
                height: '24px', 
                border: '3px solid #e5e7eb',
                borderTop: '3px solid #3b82f6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          )}
          {item.type === 'sticker' && (item.data?.emoji || item.data?.sticker?.emoji) ? (
            <div 
              className="sticker-emoji flex items-center justify-center"
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: 'transparent',
                border: 'none',
                color: item.data.textColor || item.data.sticker?.textColor || '#333333',
                fontSize: '32px',
                pointerEvents: 'none'
              }}
            >
              {item.data.emoji ?? item.data.sticker?.emoji}
            </div>
          ) : (
            <CachedImage
              src={item.data?.imageUrl || item.data?.url || item.data?.thumbnailUrl}
              fallbackUrl={item.data?.fallbackUrl || item.data?.stickerUrl}
              alt={item.data?.alt || 'Canvas image'}
              className="sticker-image-photo rounded-2xl overflow-hidden"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none' // Disable pointer events on images
              }}
              draggable={false}
            />
          )}
        </div>

        {/* Shadow layer */}
        <div className="sticker-shadow" style={{ pointerEvents: 'none' }}>
          <div className="sticker-flap no-lighting">
            {item.type === 'sticker' && (item.data?.emoji || item.data?.sticker?.emoji) ? (
              <div 
                className="sticker-shadow-image flex items-center justify-center"
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: item.data.textColor || item.data.sticker?.textColor || '#333333',
                  fontSize: '32px',
                }}
              >
                {item.data.emoji ?? item.data.sticker?.emoji}
              </div>
            ) : (
              <CachedImage
                src={item.data?.imageUrl || item.data?.url || item.data?.thumbnailUrl}
                fallbackUrl={item.data?.fallbackUrl || item.data?.stickerUrl}
                alt={item.data?.alt || 'Canvas image shadow'}
                className="sticker-shadow-image rounded-2xl overflow-hidden"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                draggable={false}
              />
            )}
          </div>
        </div>

        {/* Flap layer */}
        <div className="sticker-flap no-lighting" style={{ pointerEvents: 'none' }}>
          {item.type === 'sticker' && (item.data?.emoji || item.data?.sticker?.emoji) ? (
            <div 
              className="sticker-shadow-emoji flex items-center justify-center"
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: 'transparent',
                border: 'none',
                color: item.data.textColor || item.data.sticker?.textColor || '#333333',
                fontSize: '32px',
              }}
            >
              {item.data.emoji ?? item.data.sticker?.emoji}
            </div>
          ) : (
            <CachedImage
              src={item.data?.imageUrl || item.data?.url || item.data?.thumbnailUrl}
              fallbackUrl={item.data?.fallbackUrl || item.data?.stickerUrl}
              alt={item.data?.alt || 'Canvas image shadow'}
              className="sticker-shadow-photo rounded-2xl overflow-hidden"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              draggable={false}
            />
          )}
        </div>

        {/* Control Handles - show when hovered or dragging */}
        {(isHovered || isDragging) && (
          <>
            {/* Delete Button */}
            <div
              className="absolute"
              style={{
                top: -10,
                left: -10,
                width: 20,
                height: 20,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                border: '1px solid rgba(255, 255, 255, 0.8)',
                borderRadius: '50%',
                cursor: 'pointer',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: `rotate(${-(item.rotation || 0)}deg)`,
                transformOrigin: `${item.data.width / 2 + 10}px ${item.data.height / 2 + 10}px`,
                opacity: 0.3,
                transition: 'opacity 0.2s ease',
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onDelete) onDelete(item.id);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.3')}
              title="Delete"
            >
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                <path d="M1 1L7 7M7 1L1 7" stroke="white" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Rotation Handle - only for own items */}
            {onRotate && isOwnItem && (
              <div
                className="absolute"
                style={{
                  top: -10,
                  right: -10,
                  width: 20,
                  height: 20,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  border: '1px solid rgba(255, 255, 255, 0.8)',
                  borderRadius: '50%',
                  cursor: 'grab',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transform: `rotate(${-(item.rotation || 0)}deg)`,
                  transformOrigin: `${item.data.width / 2 + 10}px ${item.data.height / 2 + 10}px`,
                  opacity: 0.3,
                  transition: 'opacity 0.2s ease',
                }}
                onMouseDown={handleRotationMouseDown}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.3')}
                title="Drag to rotate"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M5 2C6.5 2 7.5 3 7.5 4.5" stroke="white" strokeWidth="1" strokeLinecap="round"/>
                  <path d="M6.5 3L7.5 4.5L6 5" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 8C3.5 8 2.5 7 2.5 5.5" stroke="white" strokeWidth="1" strokeLinecap="round"/>
                  <path d="M3.5 7L2.5 5.5L4 5" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Hover Overlay for other users' items */}
      <CanvasItemHoverOverlay
        itemId={item.id}
        itemOwnerId={item.data?.ownerId || (item as any).userId}
        itemOwnerName={item.data?.ownerName}
        isHovered={isHovered}
        onDoubleClick={toggleLike}
        rotation={item.rotation}
      />
    </div>
  );
});
